/**
 * Report a failure the browser saw to `POST /feedback/client-error` (8 Oct).
 *
 * Rusty: "anytime this happens log a feedback". The API logs its own failures;
 * this covers the ones it never sees — a request with no answer (offline, the
 * API down) and a screen that crashed while rendering. Fire-and-forget like
 * the page-view beacon: a plain `fetch` (not the SDK, so its error interceptor
 * cannot report the report), never awaited, never retried, throttled per tab
 * (`client-error-throttle.ts`). A lost report is a missing row in the inbox,
 * nothing more.
 */

import { createReportThrottle, isBrowserNoise } from "@/lib/client-error-throttle";

import type { ClientErrorKind } from "./generated/types.gen";

export const CLIENT_ERROR_PATH = "/feedback/client-error";
const BASE_URL = "/api";
const MAX_MESSAGE = 1000;
const MAX_PATH = 300;

const throttle = createReportThrottle();

export interface ClientErrorInput {
  kind: ClientErrorKind;
  message: string;
  /** The API path that failed, for a request with no answer. */
  endpoint?: string;
  /**
   * What the browser saw (online, visible, tries…), appended to the message
   * as `[…]` (12 Oct). Not part of the throttle key, so it cannot split one
   * failure into many reports.
   */
  detail?: string;
}

/** The screen the person is on: the route path, no query. */
export function currentScreen(): string {
  return typeof window === "undefined" ? "" : window.location.pathname.slice(0, MAX_PATH);
}

export function reportClientError(input: ClientErrorInput): void {
  try {
    const screen = currentScreen();
    const base = input.message || "";
    const message = (input.detail ? `${base} [${input.detail}]` : base).slice(0, MAX_MESSAGE);
    if (input.endpoint?.includes(CLIENT_ERROR_PATH)) {
      return;
    }
    if (input.kind === "SCRIPT" && isBrowserNoise(base)) {
      return;
    }
    if (!throttle.allow(`${input.kind}|${base.slice(0, MAX_MESSAGE)}|${screen}`, Date.now())) {
      return;
    }
    void fetch(`${BASE_URL}${CLIENT_ERROR_PATH}`, {
      method: "POST",
      body: JSON.stringify({
        kind: input.kind,
        message,
        screen,
        ...(input.endpoint ? { endpoint: input.endpoint.slice(0, MAX_PATH) } : {}),
      }),
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Reporting a failure must never become one.
  }
}
